import React from "react";
import get from "lodash/get";

import withExtendedStyles from "@sendmagic/prismic-nextjs/src/hoc/withExtendedStyles";

import PrismicImgix from "@sendmagic/prismic-nextjs/src/components/Imgix";
import PrismicLinkTag from "@sendmagic/prismic-nextjs/src/components/LinkTag";

import PrismicModalBtn from "@sendmagic/prismic-nextjs/src/components/ModalBtn";

import { textResolver } from "@sendmagic/prismic-nextjs/src/constants/text-resolver";
import { checkContentPresence } from "@sendmagic/prismic-nextjs/src/helpers/prismic";

import defaultStyles from "./main.module.scss";

const defaultObj = {};

const Feature = ({
  icon,
  title,
  feature_text,
  url_text,
  optional_feature_link_text,
  start_an_order,
  url: { url, target },
  optional_feature_link,
  show_modal_cta,
  modal_cta_text,
  modal_heading,
  modal_description,
  modal_link_text,
  modal_link,
  StartOrderBtn,
  styles,
  image_on_top,
  section_bg_color,
}) => {
  const { url: optional_feature_url, target: optional_feature_target } =
    optional_feature_link || defaultObj;
  const blockHolderClass = image_on_top
    ? `${styles.imgOnTopBlock} ${styles.featureBlock}`
    : styles.featureBlock;

  const style = { backgroundColor: section_bg_color };

  return (
    <div className={blockHolderClass}>
      <div className={styles.featureBlockHolder} style={style}>
        <div className={styles.featuresBlockHolderRow}>
          <div className={styles.featuresBlockContentHolder}>
            {checkContentPresence(title) && (
              <div className={styles.featuresBlockHolderTitle}>
                {textResolver(title, { convertLabelToModular: true })}
              </div>
            )}
            {checkContentPresence(feature_text) && (
              <div className={styles.featuresBlockHolderDescription}>
                {textResolver(feature_text, { convertLabelToModular: true })}
              </div>
            )}
            <div className={styles.featuresBlockHolderLink}>
              {(url || start_an_order) && (
                <div className={styles.featuresContentLinkParent}>
                  {start_an_order && StartOrderBtn ? (
                    <StartOrderBtn>
                      {textResolver(url_text, { convertLabelToModular: true })}
                    </StartOrderBtn>
                  ) : (
                    url && (
                      <PrismicLinkTag
                        urlText={url_text}
                        url={url}
                        target={target}
                        className={styles.featuresContentLink}
                        convertLabelToModular
                      />
                    )
                  )}
                </div>
              )}
              {optional_feature_url && (
                <div className={styles.featuresContentLinkParent}>
                  {optional_feature_link_text && (
                    <PrismicLinkTag
                      urlText={optional_feature_link_text}
                      url={optional_feature_url}
                      target={optional_feature_target}
                      className={styles.featuresContentLink}
                      convertLabelToModular
                    />
                  )}
                </div>
              )}
              {show_modal_cta && (
                <div className={styles.featuresContentLinkParent}>
                  <PrismicModalBtn
                    data={{
                      heading: modal_heading,
                      description: modal_description,
                      linkText: modal_link_text,
                      link: modal_link?.url,
                    }}
                    className={styles.featuresContentLink}
                  >
                    {textResolver(modal_cta_text, { convertLabelToModular: true })}
                  </PrismicModalBtn>
                </div>
              )}
            </div>
          </div>
          <div className={styles.featureBlockImageHolder}>
            <div className={styles.featureBlockIcon}>
              <PrismicImgix
                alt={icon.alt}
                src={icon.url}
                desktop="200px"
                mobile="90px"
                tablet="100px"
                largeDisplay="200px"
                className={styles.centerImage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeaturesHomepage = ({ doc, sliceStyles: styles = defaultStyles }) => {
  const {
    title,
    header_text,
    url,
    url_text,
    small_heading,
    internal_link_id,
    subheading,
    bg_color = "",
    image_on_top = false,
  } = doc?.primary || defaultObj;

  const style = { backgroundColor: bg_color };
  return (
    <div className={styles.featuresSection} style={style}>
      <div className={styles.featuresContainer}>
        {internal_link_id && <span id={internal_link_id} className={styles.linkId} />}
        {checkContentPresence(title) && (
          <div className={styles.titleBlock}>
            {checkContentPresence(header_text) && (
              <div className={styles.featuresSectionHeaderTitle}>
                {textResolver(header_text, { convertLabelToModular: true })}
              </div>
            )}
            <div className={styles.headingText}>
              {textResolver(title, { convertLabelToModular: true })}
            </div>
            {checkContentPresence(small_heading) && (
              <div className={styles.featuresSectionHeaderSmallTitle}>
                {textResolver(small_heading, { convertLabelToModular: true })}
              </div>
            )}
            {checkContentPresence(subheading) && (
              <div className={styles.featuresDescriptionBlock}>
                {textResolver(subheading, { convertLabelToModular: true })}
              </div>
            )}
          </div>
        )}
        <div className={styles.featureContainer}>
          {get(doc, "items")?.map((feature, idx) => (
            <Feature
              {...feature}
              key={`option-${idx}`}
              image_on_top={image_on_top}
              styles={styles}
            />
          ))}
        </div>
        {url?.url && (
          <div className={styles.sliceButtonContaoner}>
            <PrismicLinkTag
              urlText={url_text}
              url={url?.url}
              target={url?.target}
              className={styles.sliceButton}
              convertLabelToModular
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default withExtendedStyles(FeaturesHomepage, ({ appName }) => ({
  styleImport: () => {
    switch (appName) {
      case "swagmagic":
        return import(
          /* webpackChunkName: "PriFeaturesHomepageSliceSwagmagicStyles" */ "./swagmagic.module.scss"
        );
      default:
        return import(
          /* webpackChunkName: "PriFeaturesHomepageSlicedefaultStyles" */ "./main.module.scss"
        );
    }
  },
}));
